import * as api from "./client";
import { downloadBlob } from "@/utils/csvExport";

// ============================================================================
// Types
// ============================================================================

export type OptOutScope = "firm" | "edition" | "global";

export type OptOutSource =
  | "preference_centre"
  | "sendgrid_unsubscribe"
  | "spamreport"
  | "staff"
  | "legacy_import";

export type OptOutSkipReason = "duplicate" | "covered_by_broader";

export type OptOut = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  scope: OptOutScope;
  firm_ref: string | null;
  /**
   * Firm name resolved server-side from `mailing_group_member` (the same source
   * the referee preference centre uses). Authoritative for display — prefer this
   * over resolving `firm_ref` on the client. Null when the ref can't be resolved.
   */
  firm_name: string | null;
  edition: string | null;
  referee_code: string | null;
  source: OptOutSource;
  created_at: string;
};

export type OptOutPagination = {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
};

export type OptOutListResponse = {
  opt_outs: OptOut[];
  pagination: OptOutPagination;
  request_id?: string;
};

export type OptOutSortField =
  | "email"
  | "first_name"
  | "last_name"
  | "firm_ref"
  | "edition"
  | "scope"
  | "created_at";

export type OptOutListFilters = {
  email?: string;
  scope?: OptOutScope;
  edition?: string;
  firmRef?: string;
  sortField?: OptOutSortField;
  sortDirection?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

export type CreateOptOutInput = {
  email: string;
  first_name?: string;
  last_name?: string;
  scope: OptOutScope;
  firm_ref?: string;
  edition?: string;
  referee_code?: string;
};

export type CreateOptOutResponse = {
  opt_out: OptOut;
  /**
   * `true` when a higher-scope entry (edition / global) already covered this
   * request, so nothing was created. `opt_out` is the covering row.
   */
  skipped?: boolean;
  /**
   * Reason the create was skipped (present whenever `skipped` is `true`):
   * `duplicate` = an opt-out with the exact same scope/email/firm/edition
   * already exists; `covered_by_broader` = a broader edition-wide / global
   * opt-out already covers this narrower request.
   */
  skip_reason?: OptOutSkipReason | null;
  request_id?: string;
};

export type ImportOptOutsResponse = {
  imported: number;
  skipped: number;
  collapsed: number;
  errors: string[];
  request_id?: string;
};

export type ImportPreviewAction = "import" | "collapsed";

export type ImportPreviewRow = {
  row: number;
  email: string;
  scope: OptOutScope;
  firm_ref: string | null;
  edition: string | null;
  action: ImportPreviewAction;
  collapsed_by: { row: number; scope: OptOutScope } | null;
};

export type ImportValidationError = {
  row: number;
  message: string;
};

export type ValidateImportResponse = {
  total_rows: number;
  will_import: number;
  will_collapse: number;
  validation_errors: ImportValidationError[];
  preview: ImportPreviewRow[];
  request_id?: string;
};

// ============================================================================
// Service
// ============================================================================

function buildListParams(filters?: OptOutListFilters): string {
  const params = new URLSearchParams();
  if (filters?.email) params.append("email", filters.email);
  if (filters?.scope) params.append("scope", filters.scope);
  if (filters?.edition) params.append("edition", filters.edition);
  if (filters?.firmRef) params.append("firm_ref", filters.firmRef);
  if (filters?.sortField) params.append("sortField", filters.sortField);
  if (filters?.sortDirection) params.append("sortDirection", filters.sortDirection);
  if (filters?.limit !== undefined) params.append("limit", String(filters.limit));
  if (filters?.offset !== undefined) params.append("offset", String(filters.offset));
  const query = params.toString();
  return query ? `?${query}` : "";
}

async function readCsvError(response: Response): Promise<string> {
  let message = `HTTP ${response.status}`;
  try {
    const data = await response.json();
    if (data?.error && typeof data.error === "string") message = data.error;
  } catch {
    // keep default message
  }
  return message;
}

export const optOuts = {
  list: async (filters?: OptOutListFilters): Promise<OptOutListResponse> => {
    return api.get<OptOutListResponse>(`/v1/opt-outs${buildListParams(filters)}`);
  },

  create: async (input: CreateOptOutInput): Promise<CreateOptOutResponse> => {
    return api.post<CreateOptOutResponse>("/v1/opt-outs", input);
  },

  update: async (id: string, input: CreateOptOutInput): Promise<CreateOptOutResponse> => {
    return api.put<CreateOptOutResponse>(`/v1/opt-outs/${encodeURIComponent(id)}`, input);
  },

  remove: async (id: string): Promise<void> => {
    await api.del(`/v1/opt-outs/${encodeURIComponent(id)}`);
  },

  exportCsv: async (scope?: OptOutScope): Promise<void> => {
    const params = new URLSearchParams();
    if (scope) params.append("scope", scope);
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.fetchRaw(`/v1/opt-outs/export${query}`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const blob = await response.blob();
    const filename = `opt-outs-export-${new Date().toISOString().split("T")[0]}.csv`;
    downloadBlob(blob, filename);
  },

  validateImportCsv: async (csv: string): Promise<ValidateImportResponse> => {
    const response = await api.fetchRaw("/v1/opt-outs/import/validate", {
      method: "POST",
      headers: { "Content-Type": "text/csv" },
      body: csv,
    });
    if (!response.ok) {
      throw new Error(await readCsvError(response));
    }
    return response.json();
  },

  importCsv: async (csv: string): Promise<ImportOptOutsResponse> => {
    const response = await api.fetchRaw("/v1/opt-outs/import", {
      method: "POST",
      headers: { "Content-Type": "text/csv" },
      body: csv,
    });
    if (!response.ok) {
      throw new Error(await readCsvError(response));
    }
    return response.json();
  },
};
